// CAPABILITY: Account automation API key UI.
// SECURITY: shows raw API key once after create/roll; stored account metadata is
// display-only (prefix/timestamps), never raw key material.

import { doc, getDoc } from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { firestore } from "../../../firebase";
import {
  createApiKey,
  deleteApiKey,
  mapApiKeyError,
  normalizeApiKeyMetadata,
  rollApiKey,
} from "../../services/apiKeyService";

function Banner({ tone, children }) {
  if (!children) return null;
  const cls =
    tone === "success"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : "border-rose-200 bg-rose-50 text-rose-700";
  return <div className={`rounded-lg border px-4 py-3 text-sm font-medium ${cls}`}>{children}</div>;
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function initialMetadata(accountData) {
  return normalizeApiKeyMetadata(accountData?.api_key || {});
}

export default function ApiKeySettings({ onOpenBilling } = {}) {
  const { accountData, chatEntitled, currentUser } = useAuth();
  const [metadata, setMetadata] = useState(() => initialMetadata(accountData));
  const [revealedKey, setRevealedKey] = useState("");
  const [state, setState] = useState({ busy: false, error: "", success: "" });

  useEffect(() => {
    let isMounted = true;
    setMetadata(initialMetadata(accountData));

    if (currentUser?.uid) {
      getDoc(doc(firestore, "accounts", currentUser.uid))
        .then((snap) => {
          if (isMounted && snap.exists()) setMetadata(normalizeApiKeyMetadata(snap.data()?.api_key || {}));
        })
        .catch((error) => {
          console.warn(`API_KEY_METADATA_LOAD_FAILED uid=${currentUser.uid} reason=${error.message}`);
        });
    }

    return () => {
      isMounted = false;
    };
  }, [accountData, currentUser]);

  const runAction = async (action, successMessage) => {
    setState({ busy: true, error: "", success: "" });
    try {
      const result = await action();
      setMetadata(result.metadata);
      setRevealedKey(result.apiKey || "");
      setState({ busy: false, error: "", success: successMessage });
    } catch (error) {
      setState({ busy: false, error: mapApiKeyError(error), success: "" });
    }
  };

  const handleCreate = () => runAction(createApiKey, "API key created. Copy it now; it will not be shown again.");

  const handleRoll = () => {
    if (!window.confirm("Roll this API key? The old key will stop working immediately.")) return;
    runAction(rollApiKey, "API key rolled. Copy the new key now; it will not be shown again.");
  };

  const handleDelete = () => {
    if (!window.confirm("Delete this API key? Automation using it will stop immediately.")) return;
    runAction(deleteApiKey, "API key deleted.");
  };

  const copyRevealedKey = async () => {
    if (!revealedKey) return;
    try {
      await navigator.clipboard.writeText(revealedKey);
      setState((prev) => ({ ...prev, success: "API key copied." }));
    } catch (_error) {
      setState((prev) => ({ ...prev, error: "Copy failed. Select the key text manually." }));
    }
  };

  const active = metadata.active === true;

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-base font-semibold text-slate-950">Automation API key</h3>
          <p className="mt-1 text-sm leading-6 text-slate-500">
            Create pages by JSON POST. API access is included with the Pro plan.
          </p>
        </div>
        {!chatEntitled && (
          <button type="button" className="primary-button" onClick={onOpenBilling}>
            Upgrade to Pro
          </button>
        )}
      </div>

      <div className="mt-4 space-y-4">
        <Banner tone="error">{state.error}</Banner>
        <Banner tone="success">{state.success}</Banner>

        {!chatEntitled && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Your current plan cannot create or roll API keys. Existing keys are checked against plan status on every request.
          </div>
        )}

        {revealedKey && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
            <p className="text-sm font-semibold text-emerald-900">Copy this key now</p>
            <p className="mt-1 text-xs leading-5 text-emerald-800">
              For security, VdoPage stores only a hash and cannot show this full key again.
            </p>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <code className="min-w-0 flex-1 overflow-x-auto rounded-md bg-white px-3 py-2 text-xs text-slate-900 ring-1 ring-emerald-200">
                {revealedKey}
              </code>
              <button type="button" className="secondary-button" onClick={copyRevealedKey}>
                Copy
              </button>
            </div>
          </div>
        )}

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm">
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Status</p>
              <p className="mt-1 font-medium text-slate-900">{active ? "Active" : "No active key"}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Prefix</p>
              <p className="mt-1 font-mono text-slate-900">{active ? `${metadata.prefix}…` : "—"}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Created</p>
              <p className="mt-1 text-slate-900">{formatDate(metadata.created_at)}</p>
            </div>
          </div>
          {metadata.rolled_at && (
            <p className="mt-3 text-xs text-slate-500">Last rolled: {formatDate(metadata.rolled_at)}</p>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          {!active ? (
            <button
              type="button"
              className="primary-button"
              disabled={!chatEntitled || state.busy}
              onClick={handleCreate}
            >
              {state.busy ? "Creating…" : "Create API key"}
            </button>
          ) : (
            <>
              <button
                type="button"
                className="secondary-button"
                disabled={!chatEntitled || state.busy}
                onClick={handleRoll}
              >
                {state.busy ? "Rolling…" : "Roll key"}
              </button>
              <button type="button" className="secondary-button" disabled={state.busy} onClick={handleDelete}>
                {state.busy ? "Deleting…" : "Delete key"}
              </button>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
